<script lang="ts">
import type { DialogContentEmits, DialogContentProps, DialogRootEmits, DialogRootProps } from 'reka-ui'
import type { EmitsToProps } from '../types/utils'

export interface ModalProps extends Pick<DialogRootProps, 'open' | 'defaultOpen'> {
  title?: string
  description?: string
  content?: Omit<DialogContentProps, 'as' | 'asChild' | 'forceMount'> & Partial<EmitsToProps<DialogContentEmits>> & {
    class?: string | object | Array<string | object>
  }
  portal?: boolean | string | HTMLElement
  isDismissible?: boolean
  class?: string | object
  size?: 'sm' | 'md' | 'lg' | 'xl'
  position?: 'center' | 'right' | 'top'
  fullscreen?: boolean
}

export interface ModalEmits extends DialogRootEmits {
  afterLeave: []
  afterEnter: []
  closePrevent: []
}

export interface ModalSlots {
  default?: (props: { open: boolean }) => any
  content?: (props: { close: () => void }) => any
  header?: (props: { close: () => void }) => any
  title?: (props?: object) => any
  body?: (props: { close: () => void }) => any
  footer?: (props: { close: () => void }) => any
  leftPanel?: (props?: object) => any
}

const ui = {
  content: 'bg-white shadow-lg border border-secondary-700 rounded-lg flex flex-row overflow-hidden',
  mainContent: 'flex flex-col',
  overlay: 'fixed inset-0 bg-primary-900/35',
  header: 'flex font-500 items-center justify-between px-3 py-2 border-b border-secondary-300',
  wrapper: 'flex fixed inset-0',
  body: 'flex-1 overflow-y-auto text-md',
  footer: 'flex items-center justify-end p-3 gap-2 border-t border-secondary-200',
  size: {
    sm: 'max-w-104 w-104',
    md: 'max-w-125 w-125',
    lg: 'max-w-187.5 w-187.5 max-h-[90vh]',
    xl: 'max-w-275 w-275 max-h-[90vh]',
  },
  position: {
    top: {
      wrapper: 'items-start justify-center pt-25vh',
    },
    center: {
      wrapper: 'items-center justify-center p-7.5',
    },
    right: {
      wrapper: 'h-full justify-end p-2.5',
    },
  },
  fullscreen: {
    wrapper: 'p-0',
    content: 'border-none rounded-none shadow-none w-screen h-screen max-w-screen max-h-screen',
  },
}
</script>

<script setup lang="ts">
import { reactivePick } from '@vueuse/core'
import { DialogClose, DialogContent, DialogOverlay, DialogPortal, DialogRoot, DialogTitle, DialogTrigger, useForwardPropsEmits, VisuallyHidden } from 'reka-ui'
import { computed, toRef } from 'vue'
import { usePortal } from '../composables/usePortal'
import icons from '../styles/icons'
import AwButton from './AwButton.vue'

const props = withDefaults(defineProps<ModalProps>(), {
  close: true,
  portal: true,
  transition: true,
  modal: true,
  dismissible: true,
  position: 'center',
})
const emits = defineEmits<ModalEmits>()
const slots = defineSlots<ModalSlots>()

const rootProps = useForwardPropsEmits(reactivePick(props, 'open', 'defaultOpen'), emits)
const portalProps = usePortal(toRef(() => props.portal))
const contentProps = computed(() => {
  const content = props.content
  if (!content) {
    return undefined
  }
  const { class: _class, ...rest } = content
  return rest
})
const contentEvents = computed(() => {
  const defaultEvents = {
    closeAutoFocus: (e: Event) => e.preventDefault(),
  }
  if (!props.isDismissible) {
    const events = ['interactOutside', 'escapeKeyDown']
    return events.reduce((acc, curr) => {
      acc[curr] = (e: Event) => {
        e.preventDefault()
        emits('closePrevent')
      }
      return acc
    }, defaultEvents as Record<typeof events[number] | keyof typeof defaultEvents, (e: Event) => void>)
  }

  return defaultEvents
})

const contentClass = computed(() => {
  return [ui.content, props.content?.class]
})
const hasLeftPanel = computed(() => Boolean(slots.leftPanel))
const mainContentClass = computed(() => {
  // `max-h-[90vh]` (carried by size `lg`) is meant for centered modals; on a full-height
  // right-side panel it leaves dead space under the footer, so fill the panel instead.
  return [ui.mainContent, hasLeftPanel.value && 'shrink-0', props.size && ui.size[props.size], props.position === 'right' && 'h-full !max-h-none']
})
const wrapperClass = computed(() => {
  if (props.fullscreen) {
    return [ui.wrapper, ui.fullscreen.wrapper]
  }
  return [ui.wrapper, ui.position[props.position].wrapper]
})
</script>

<template>
  <DialogRoot v-slot="{ open, close }" v-bind="rootProps">
    <DialogTrigger v-if="!!slots.default" as-child :class="props.class">
      <slot :open />
    </DialogTrigger>

    <DialogPortal v-if="open" v-bind="portalProps">
      <DialogOverlay :class="ui.overlay" />

      <div :class="wrapperClass">
        <DialogContent :class="contentClass" v-bind="contentProps" @after-enter="emits('afterEnter')" @after-leave="emits('afterLeave')" v-on="contentEvents">
          <slot v-if="!!slots.leftPanel" name="leftPanel" />

          <div id="main-content" data-aw-teleport-root :class="mainContentClass">
            <VisuallyHidden v-if="!!slots.content && (title || !!slots.title)">
              <DialogTitle class="text-[16px] leading-[20px]" v-if="title || !!slots.title">
                <slot name="title">
                  {{ title }}
                </slot>
              </DialogTitle>
            </VisuallyHidden>

            <slot name="content" :close>
              <div v-if="!!slots.header || (title || !!slots.title)" :class="ui.header">
                <slot name="header" :close>
                  <DialogTitle class="text-[16px] leading-[20px]" v-if="title || !!slots.title">
                    <slot name="title">
                      {{ title }}
                    </slot>
                  </DialogTitle>

                  <DialogClose as-child>
                    <AwButton
                      :icon="icons.closeBold"
                      size="sm"
                      color="secondary"
                      variant="ghost"
                    />
                  </DialogClose>
                </slot>
              </div>

              <div v-if="!!slots.body" :class="ui.body">
                <slot name="body" :close />
              </div>

              <div v-if="!!slots.footer" :class="ui.footer">
                <slot name="footer" :close />
              </div>
            </slot>
          </div>
        </DialogContent>
      </div>
    </DialogPortal>
  </DialogRoot>
</template>
